import type { Certification, ReadinessProfile } from '../../types/readiness';
import {
  formatDateOnlyLocal,
  normalizeStateCertificationLevel,
  normalizeStateCode,
  toDateOnlyLocal,
  type HeldCertification,
} from '../constants/certifications';
import { resolveCertVerification } from '../credentials/certificationChecks';
import { countLabel } from '../text/plural';
import { addDaysToDate, timeZoneForState, zonedDateKey, zonedWallTimeToUtcMs } from '../scheduling/time';

const NREMT_PATTERNS = [/nremt/i, /emt-?b/i, /emt-?p/i, /paramedic/i, /aemt/i];
/** Avoid matching "driver's license" as state EMS license. */
const STATE_LICENSE_PATTERNS = [/state\s*license/i, /emt\s*license/i, /\bstate\s+ems\b/i];
const BLS_PATTERNS = [/\bbls\b/i, /basic life support/i, /\bcpr\b/i];
const ACLS_PATTERNS = [/\bacls\b/i];
const PALS_PATTERNS = [/\bpals\b/i];
const PHTLS_PATTERNS = [/\bphtls\b/i];

export function normalizeCertName(raw: unknown): string {
  if (typeof raw === 'string') return raw.trim();
  if (raw && typeof raw === 'object' && typeof (raw as { name?: string }).name === 'string') {
    return (raw as { name: string }).name.trim();
  }
  return '';
}

export function extractCertifications(profile: ReadinessProfile): Certification[] {
  const raw = profile.professional?.certifications ?? [];
  return raw.map((c, i) => {
    if (typeof c === 'string') {
      return { id: `cert-${i}`, name: c.trim() };
    }
    const name = normalizeCertName(c);
    const expiry = (c as { expiryDate?: string; expiresAt?: string }).expiryDate
      || (c as { expiresAt?: string }).expiresAt;
    // Server-written result for this exact document only — never a client-written status.
    const aiVerification = resolveCertVerification(c, profile.certificationChecks);
    const state = (c as { state?: unknown }).state;
    const level = (c as { level?: unknown }).level;
    return {
      id: (c as { id?: string }).id || `cert-${i}`,
      name,
      expiryDate: expiry,
      ...(typeof state === 'string' && state.trim() ? { state: state.trim() } : {}),
      ...(typeof level === 'string' && level.trim() ? { level: level.trim() } : {}),
      fileUrl: (c as { fileUrl?: string; url?: string }).fileUrl
        || (c as { url?: string }).url,
      ...(aiVerification ? { aiVerification } : {}),
    };
  }).filter((c) => c.name.length > 0);
}

export function certMatchesPattern(name: string, patterns: RegExp[]): boolean {
  return patterns.some((p) => p.test(name));
}

/**
 * The real instant of a 'now' Date, in milliseconds. Kept for older callers: before 2026-10-13 the
 * server shifted the clock by the candidate's zone and attached the unshifted instant here. The
 * scoring clock below never shifts, so this is now just the instant.
 */
export const REAL_INSTANT_KEY = '__realInstantMs';
export function instantMsOf(now: Date): number {
  const real = (now as unknown as Record<string, unknown>)[REAL_INSTANT_KEY];
  return typeof real === 'number' && Number.isFinite(real) ? real : now.getTime();
}

/**
 * THE SCORING CLOCK (2026-10-13 logic audit). Every date rule in scoring ("expired", "days until it
 * expires", "active in the last 30 days") is read on the EMT's own calendar: the time zone of their
 * address state, or UTC when no state is on file. The EMT's browser and the server use the same zone,
 * so the employer and the EMT always count the same day, wherever the device is. The Date keeps the
 * real instant (never shifted) and carries the zone as a hidden property.
 */
export const SCORING_ZONE_KEY = '__scoringZone';
const DAY_MS = 24 * 60 * 60 * 1000;

/** The zone scoring reads an EMT's dates in: their address state's zone, else UTC. */
export function scoringZoneFor(profile: unknown): string {
  const state = (profile as { address?: { state?: unknown } } | null | undefined)?.address?.state;
  return timeZoneForState(state) ?? 'UTC';
}

function zoneOf(now: Date): string | null {
  const z = (now as unknown as Record<string, unknown>)[SCORING_ZONE_KEY];
  return typeof z === 'string' && z ? z : null;
}

/** `now` for scoring this profile. A clock that already carries a zone keeps it. */
export function scoringClock(profile: unknown, now: Date | number = Date.now()): Date {
  const existing = typeof now === 'number' ? null : zoneOf(now);
  const ms = typeof now === 'number' ? now : instantMsOf(now);
  const d = new Date(ms);
  Object.defineProperty(d, SCORING_ZONE_KEY, { value: existing ?? scoringZoneFor(profile), enumerable: false });
  return d;
}

/** The calendar day (YYYY-MM-DD) an instant falls on, on the scoring clock's calendar. */
export function scoringDateKey(ms: number, now: Date): string {
  const zone = zoneOf(now);
  return zone ? zonedDateKey(ms, zone) : toDateOnlyLocal(new Date(ms));
}

/** Today on the scoring clock's calendar. */
export function scoringToday(now: Date): string {
  return scoringDateKey(instantMsOf(now), now);
}

/**
 * The instant the EMT's next calendar day starts (their midnight). Every date rule in scoring can only
 * change at that moment, so a stored score is good until then (the server refreshes it right after).
 */
export function nextScoringDayStartMs(now: Date): number {
  const zone = zoneOf(now) ?? 'UTC';
  const [y, m, d] = scoringToday(now).split('-').map(Number);
  const next = addDaysToDate(y, m, d, 1);
  return zonedWallTimeToUtcMs(next.year, next.month, next.day, 0, 0, zone);
}

/** Whole calendar days from `fromYmd` to `toYmd` (zone-free date arithmetic). */
export function calendarDaysBetween(fromYmd: string, toYmd: string): number {
  const [fy, fm, fd] = fromYmd.split('-').map(Number);
  const [ty, tm, td] = toYmd.split('-').map(Number);
  return Math.round((Date.UTC(ty, tm - 1, td) - Date.UTC(fy, fm - 1, fd)) / DAY_MS);
}

/**
 * THE COUNTING RULE (2026-10-13 logic audit): a credential counts toward a score, a match or a
 * requirement only when (1) it is on the profile, (2) its document passed our document review (the
 * trusted server result, "Documents Reviewed") and (3) it has not expired on the EMT's calendar.
 * Pending, rejected, never-uploaded and expired credentials never count.
 */
export function certCounts(c: Certification, now: Date): boolean {
  return c.aiVerification?.status === 'under_review' && !isExpired(c.expiryDate, now);
}

/** Why a credential on the profile does not count (null when it counts). */
export function certNotCountingReason(c: Certification, now: Date): 'expired' | 'not_reviewed' | null {
  if (c.aiVerification?.status === 'under_review') return isExpired(c.expiryDate, now) ? 'expired' : null;
  return 'not_reviewed';
}

/**
 * The profile's credentials that count (certCounts), as name + issuing state + level, the shape the
 * job-matching helpers take. `now` should be the scoring clock.
 */
export function countingCertificationEntries(profile: ReadinessProfile, now: Date): HeldCertification[] {
  const certs = extractCertifications(profile);
  return certs
    .filter((c) => certCounts(c, now))
    .map((c) => ({ name: c.name, state: normalizeStateCode(c.state), level: normalizeStateCertificationLevel(c.level) }));
}

/** Matching patterns ignore credentials that do not count: is one that counts on file? */
export function hasCountingCert(certs: Certification[], patterns: RegExp[], now: Date): boolean {
  return certs.some((c) => certMatchesPattern(c.name, patterns) && certCounts(c, now));
}

export function hasActiveCert(certs: Certification[], patterns: RegExp[], now = new Date()): boolean {
  const matching = certs.filter((c) => certMatchesPattern(c.name, patterns));
  if (matching.length === 0) return false;
  return matching.some((c) => !isExpired(c.expiryDate, now));
}

/** Date-only compare (YYYY-MM-DD) — no UTC midnight shift. */
export function isExpired(expiryDate: string | undefined, now = new Date()): boolean {
  if (!expiryDate) return false;
  const ymd = expiryDate.trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
    const d = new Date(expiryDate);
    if (Number.isNaN(d.getTime())) return false;
    return d.getTime() < instantMsOf(now);
  }
  return ymd < scoringToday(now);
}

export function daysUntilExpiry(expiryDate: string | undefined, now = new Date()): number | null {
  if (!expiryDate) return null;
  const ymd = expiryDate.trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
    const d = new Date(expiryDate);
    if (Number.isNaN(d.getTime())) return null;
    return Math.ceil((d.getTime() - instantMsOf(now)) / (24 * 60 * 60 * 1000));
  }
  return calendarDaysBetween(scoringToday(now), ymd);
}

export function formatExpiryLabel(expiryDate: string | undefined): string {
  const days = daysUntilExpiry(expiryDate);
  if (days == null) return 'no expiry on file';
  if (days < 0) return `expired ${countLabel(Math.abs(days), 'day')} ago`;
  if (days === 0) return 'expires today';
  if (days <= 30) return `expires in ${countLabel(days, 'day')}`;
  return `expires ${formatDateOnlyLocal(expiryDate!, undefined, { month: 'short', year: 'numeric' })}`;
}

export {
  NREMT_PATTERNS,
  STATE_LICENSE_PATTERNS,
  BLS_PATTERNS,
  ACLS_PATTERNS,
  PALS_PATTERNS,
  PHTLS_PATTERNS,
};
